import type { Catalogs, PlainKey, Translation } from "@amtana/i18n/catalog";

// The prospect table, detail and screens' words, in English and Spanish: one
// key per string, read through `useProspectCopy` (behavior/use-prospect-copy.ts).
// The scoring screen keeps its own catalogue in scoring-copy.ts.

export type ProspectCopyKey = keyof typeof EN;
export type ProspectCatalog = typeof EN;

const EN = {
  listScreenTitle: "Prospects · {product}",
  detailScreenTitle: "Prospect · {product}",
  navProspects: "Prospects",
  // The home's "Latest prospects" column (S39.01).
  latestTitle: "Latest prospects",
  latestSeeAll: "See all",
  latestEmptyTitle: "No prospects yet",
  latestEmptyDescription: "The leads your analyses find show up here.",
  latestErrorTitle: "We couldn't load your prospects",
  latestErrorDescription: "Check your connection, then try again.",
  latestRetry: "Try again",
  breadcrumbHome: "Home",
  breadcrumbProspects: "Prospects",
  prospect: "Prospect",
  prospectsTitle: "Prospects",
  prospectsDescription:
    "Manage and explore all your prospects in one place. Prospects are shared across projects.",
  columnNameUsername: "Name & username",
  columnFirstName: "First name",
  columnLastName: "Last name",
  columnEmail: "Email",
  columnEmailStatus: "Email status",
  emailStatusNotChecked: "Not checked",
  columnPhone: "Phone",
  columnWebsite: "Website",
  columnFollowers: "Followers",
  columnFollowing: "Following",
  columnBiography: "Biography",
  columnPlatform: "Platform",
  columnQualification: "Qualification",
  columnVerified: "Verified",
  columnBusiness: "Business",
  columnGender: "Gender",
  columnEntityType: "Entity type",
  columnCountry: "Country",
  columnLanguage: "Language",
  columnNiche: "Niche",
  columnCategory: "Category",
  columnAddress: "Address",
  columnRating: "Rating",
  columnReviews: "Reviews",
  qualificationInterested: "Interested",
  qualificationNotInterested: "Not interested",
  qualificationWrongPerson: "Wrong person",
  qualificationDoNotContact: "Do not contact",
  qualificationOutOfOffice: "Out of office",
  yes: "Yes",
  no: "No",
  tableLabel: "Prospects",
  searchPlaceholder: "Search by name, username or email",
  bulkActions: "Bulk actions",
  selectedCount: { one: "1 selected", other: "{count} selected" },
  qualificationGroup: "Qualification",
  clearQualification: "Clear qualification",
  markDoNotContact: "Mark do not contact",
  delete: "Delete",
  filters: "Filters",
  hasEmail: "Has email",
  hasPhone: "Has phone",
  notQualifiedYet: "Not qualified yet",
  export: "Export",
  enrich: "Enrich",
  columns: "Columns",
  noMatchTitle: "No prospects match",
  emptyTitle: "No prospects yet",
  emptyAnalysisDescription: "Prospects appear here as the analysis finds them.",
  emptyDescription: "Run an analysis to fill your prospect database.",
  exportSourceName: "Prospects",
  close: "Close",
  prospectsUpdated: { one: "1 prospect updated", other: "{count} prospects updated" },
  prospectsDeleted: { one: "1 prospect deleted", other: "{count} prospects deleted" },
  couldNotUpdate: "Could not update prospects",
  followersCount: { one: "{followers} follower", other: "{followers} followers" },
  pillDoNotContact: "Do not contact",
  pillBusiness: "Business",
  pillPerson: "Person",
  pillVerified: "Verified",
  detailEmail: "Email",
  detailEmailStatus: "Email status",
  detailPhone: "Phone",
  detailWebsite: "Website",
  detailCountry: "Country",
  detailLanguage: "Language",
  detailNiche: "Niche",
  detailCategory: "Category",
  detailAddress: "Address",
  detailRating: "Rating",
  ratingWithReviews: { one: "{rating} ({reviews} review)", other: "{rating} ({reviews} reviews)" },
  detailFollowing: "Following",
  detailLinkedTo: "Linked to",
  analysisCount: { one: "1 analysis", other: "{count} analyses" },
} as const;

const ES: Translation<ProspectCatalog> = {
  listScreenTitle: "Prospectos · {product}",
  detailScreenTitle: "Prospecto · {product}",
  navProspects: "Prospectos",
  latestTitle: "Últimos prospectos",
  latestSeeAll: "Ver todos",
  latestEmptyTitle: "Aún no tienes prospectos",
  latestEmptyDescription: "Los leads que encuentren tus análisis aparecen aquí.",
  latestErrorTitle: "No pudimos cargar tus prospectos",
  latestErrorDescription: "Revisa tu conexión y vuelve a intentarlo.",
  latestRetry: "Reintentar",
  breadcrumbHome: "Inicio",
  breadcrumbProspects: "Prospectos",
  prospect: "Prospecto",
  prospectsTitle: "Prospectos",
  prospectsDescription:
    "Gestiona y explora todos tus prospectos en un solo lugar. Los prospectos se comparten entre proyectos.",
  columnNameUsername: "Nombre y usuario",
  columnFirstName: "Nombre",
  columnLastName: "Apellido",
  columnEmail: "Correo",
  columnEmailStatus: "Estado del correo",
  emailStatusNotChecked: "No comprobado",
  columnPhone: "Teléfono",
  columnWebsite: "Sitio web",
  columnFollowers: "Seguidores",
  columnFollowing: "Seguidos",
  columnBiography: "Biografía",
  columnPlatform: "Plataforma",
  columnQualification: "Calificación",
  columnVerified: "Verificado",
  columnBusiness: "Negocio",
  columnGender: "Género",
  columnEntityType: "Tipo de entidad",
  columnCountry: "País",
  columnLanguage: "Idioma",
  columnNiche: "Nicho",
  columnCategory: "Categoría",
  columnAddress: "Dirección",
  columnRating: "Valoración",
  columnReviews: "Reseñas",
  qualificationInterested: "Interesado",
  qualificationNotInterested: "No interesado",
  qualificationWrongPerson: "Persona equivocada",
  qualificationDoNotContact: "No contactar",
  qualificationOutOfOffice: "Fuera de la oficina",
  yes: "Sí",
  no: "No",
  tableLabel: "Prospectos",
  searchPlaceholder: "Busca por nombre, usuario o correo",
  bulkActions: "Acciones en bloque",
  selectedCount: { one: "1 seleccionado", other: "{count} seleccionados" },
  qualificationGroup: "Calificación",
  clearQualification: "Quitar calificación",
  markDoNotContact: "Marcar como no contactar",
  delete: "Eliminar",
  filters: "Filtros",
  hasEmail: "Tiene correo",
  hasPhone: "Tiene teléfono",
  notQualifiedYet: "Sin calificar todavía",
  export: "Exportar",
  enrich: "Enriquecer",
  columns: "Columnas",
  noMatchTitle: "Ningún prospecto coincide",
  emptyTitle: "Todavía no hay prospectos",
  emptyAnalysisDescription: "Los prospectos aparecen aquí a medida que el análisis los encuentra.",
  emptyDescription: "Ejecuta un análisis para llenar tu base de prospectos.",
  exportSourceName: "Prospectos",
  close: "Cerrar",
  prospectsUpdated: { one: "1 prospecto actualizado", other: "{count} prospectos actualizados" },
  prospectsDeleted: { one: "1 prospecto eliminado", other: "{count} prospectos eliminados" },
  couldNotUpdate: "No se pudieron actualizar los prospectos",
  followersCount: { one: "{followers} seguidor", other: "{followers} seguidores" },
  pillDoNotContact: "No contactar",
  pillBusiness: "Negocio",
  pillPerson: "Persona",
  pillVerified: "Verificado",
  detailEmail: "Correo",
  detailEmailStatus: "Estado del correo",
  detailPhone: "Teléfono",
  detailWebsite: "Sitio web",
  detailCountry: "País",
  detailLanguage: "Idioma",
  detailNiche: "Nicho",
  detailCategory: "Categoría",
  detailAddress: "Dirección",
  detailRating: "Valoración",
  ratingWithReviews: { one: "{rating} ({reviews} reseña)", other: "{rating} ({reviews} reseñas)" },
  detailFollowing: "Seguidos",
  detailLinkedTo: "Vinculado a",
  analysisCount: { one: "1 análisis", other: "{count} análisis" },
};

export const PROSPECT_CATALOGS: Catalogs<ProspectCatalog> = { en: EN, es: ES };

export type ProspectPlainKey = PlainKey<ProspectCatalog>;
